// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet } from 'react-native';
import { Stack } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

type SubscriptionRow = {
  userId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

/**
 * Admin-only diagnostic listing of every subscription (see AdminMenu in
 * app/layout/RootLayoutNav.tsx). Unlike /subscription, this stays admin-only — developers do not
 * get this entry, since it carries every user's Stripe identifiers, not just the caller's own.
 */
export default function SubscriptionAdminScreen() {
  const { t, locale } = useI18n();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const requestIdRef = useRef<string | null>(null);

  const [rows, setRows] = useState<SubscriptionRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin || !user?.token) {
      setLoading(false);
      return;
    }

    const socket = socketRef.current;
    socket.setAuthToken(user.token);

    const requestId = generateId();
    requestIdRef.current = requestId;
    setLoading(true);
    setError(null);
    socket.send({ type: 'subscription.admin.list.request', requestId });

    const unsubscribe = socket.onMessage((message) => {
      const payload = message as { type?: string; requestId?: string };
      if (payload.requestId !== requestIdRef.current) {
        return;
      }
      if (payload.type === 'subscription.admin.list.ack') {
        const ack = message as { items?: SubscriptionRow[] };
        setRows(ack.items ?? []);
        setLoading(false);
      } else if (payload.type === 'subscription.admin.list.error') {
        setError(t('subscriptionAdminPage.error'));
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isAdmin, t, user?.token]);

  const formatDate = (value: string | null) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString(locale);
    } catch {
      return value;
    }
  };

  return (
    <>
      <Stack.Screen options={{ title: t('subscriptionAdminPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('subscriptionAdminPage.title')}</Text>

        {!isAdmin ? (
          <Text style={styles.paragraph}>{t('subscriptionAdminPage.forbidden')}</Text>
        ) : loading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" />
            <Text style={styles.paragraph}>{t('subscriptionAdminPage.loading')}</Text>
          </View>
        ) : error ? (
          <Text style={styles.errorText}>{error}</Text>
        ) : !rows || rows.length === 0 ? (
          <Text style={styles.paragraph}>{t('subscriptionAdminPage.empty')}</Text>
        ) : (
          rows.map((row) => (
            <View key={row.stripeSubscriptionId ?? row.userId} style={styles.row}>
              <Text style={styles.rowUserId}>{row.userId}</Text>
              <View style={styles.rowLine}>
                <Text style={styles.rowLabel}>{t('subscriptionAdminPage.status')}</Text>
                <Text style={styles.rowValue}>
                  {row.status ?? '—'}
                  {row.cancelAtPeriodEnd ? ` (${t('subscriptionAdminPage.cancelling')})` : ''}
                </Text>
              </View>
              <View style={styles.rowLine}>
                <Text style={styles.rowLabel}>{t('subscriptionAdminPage.price')}</Text>
                <Text style={styles.rowValue}>{row.stripePriceId ?? '—'}</Text>
              </View>
              <View style={styles.rowLine}>
                <Text style={styles.rowLabel}>{t('subscriptionAdminPage.renews')}</Text>
                <Text style={styles.rowValue}>{formatDate(row.currentPeriodEnd)}</Text>
              </View>
              <View style={styles.rowLine}>
                <Text style={styles.rowLabel}>{t('subscriptionAdminPage.updated')}</Text>
                <Text style={styles.rowValue}>{formatDate(row.updatedAt)}</Text>
              </View>
              <View style={styles.rowLine}>
                <Text style={styles.rowLabel}>{t('subscriptionAdminPage.stripeIds')}</Text>
                <Text style={styles.rowValueSmall}>
                  {row.stripeCustomerId ?? '—'} / {row.stripeSubscriptionId ?? '—'}
                </Text>
              </View>
            </View>
          ))
        )}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 20,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  errorText: {
    fontSize: 14,
    color: '#fca5a5',
  },
  row: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 8,
    padding: 12,
    gap: 4,
  },
  rowUserId: {
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 4,
  },
  rowLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  rowLabel: {
    fontSize: 12,
    opacity: 0.7,
  },
  rowValue: {
    fontSize: 12,
    fontWeight: '600',
  },
  rowValueSmall: {
    fontSize: 11,
    opacity: 0.8,
  },
});
